import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  Target,
  CheckCircle,
  TrendingUp,
  Shield,
  Zap,
  Clock,
  Users,
  Globe,
  Mail,
  Phone,
  MapPin,
  Award,
  Database,
  Lock,
  Smartphone,
  Activity,
  Flag,
  BookOpen,
  Lightbulb,
  Palette,
  Type,
  Camera,
  Play,
  Pause,
  Upload,
  Filter,
  Video,
  Image,
  Mic,
  Edit3,
  Copy,
  Download,
  Eye,
  BarChart3,
  PieChart,
  LineChart,
  Share2,
  Heart,
  ThumbsUp,
  MessageCircle,
  Send,
  Plus,
  Settings,
  Search
} from 'lucide-react';
// Layout simple sans authentification
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import CountUp from '../components/CountUp';
import Logo from '../components/Logo';
import '../styles/background-effects.css';

// Aurora Background Effect Component - Fixed Full Height avec couleurs du logo "G efficience globale"
const AuroraBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
    {/* Gradient de fond avec les couleurs teal du logo */}
    <div className="absolute inset-0 bg-gradient-to-br from-teal-light via-teal-dark to-teal-800 opacity-10 teal-breathe"></div>
    
    {/* Premier cercle Aurora - Teal clair avec animation float */}
    <div className="absolute -top-40 -left-40 w-80 h-80 bg-teal-light rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-float"></div>
    
    {/* Deuxième cercle Aurora - Teal foncé avec animation pulse personnalisée */}
    <div className="absolute -bottom-40 -right-40 w-80 h-80 bg-teal-dark rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-pulse-teal animation-delay-1000"></div>
    
    {/* Troisième cercle Aurora - Teal 800 avec animation drift */}
    <div className="absolute top-40 right-40 w-80 h-80 bg-teal-800 rounded-full mix-blend-multiply filter blur-3xl opacity-30 aurora-drift animation-delay-2000"></div>
    
    {/* Cercle supplémentaire pour plus de dynamisme - Teal 600 avec animation combinée */}
    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 aurora-float animation-delay-1500"></div>
    
    {/* Éléments blancs pour plus de luminosité */}
    <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-15 white-glow animation-delay-500"></div>
    <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-12 teal-white-flow animation-delay-1200"></div>
    <div className="absolute top-3/4 left-3/4 w-56 h-56 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-18 white-glow animation-delay-1800"></div>
    
    {/* Éléments blancs supplémentaires pour plus de richesse */}
    <div className="absolute top-1/6 right-1/6 w-48 h-48 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-14 white-sparkle animation-delay-300"></div>
    <div className="absolute bottom-1/6 left-1/6 w-60 h-60 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-16 white-wave animation-delay-800"></div>
    <div className="absolute top-2/3 right-2/3 w-52 h-52 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-13 white-pulse-glow animation-delay-1400"></div>
    <div className="absolute bottom-2/3 left-2/3 w-44 h-44 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-17 white-float-gentle animation-delay-1600"></div>
    <div className="absolute top-1/2 right-1/3 w-68 h-68 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-11 white-sparkle animation-delay-2000"></div>
    <div className="absolute bottom-1/2 left-1/3 w-40 h-40 bg-white rounded-full mix-blend-multiply filter blur-3xl opacity-19 white-wave animation-delay-2200"></div>
    
    {/* Overlay de gradient teal avec blanc pour un effet plus subtil */}
    <div className="absolute inset-0 teal-gradient-overlay"></div>
  </div>
);

const CommunicationCreationPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Aurora Background Effect */}
      <AuroraBackground />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center space-x-2">
                <Logo className="w-16 h-16" />
                <span className="text-xl font-bold text-gray-900">omnia-eg</span>
              </Link>
            </motion.div>
            
            <motion.div 
              className="hidden md:flex items-center space-x-8"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Link to="/" className="text-gray-600 hover:text-teal-dark transition-colors">
                Accueil
              </Link>
              <Link to="/document-management" className="text-gray-600 hover:text-teal-dark transition-colors">
                Gestion Documentaire
              </Link>
              <Link to="/strategic-writing" className="text-gray-600 hover:text-teal-dark transition-colors">
                Rédaction Stratégique
              </Link>
              <Link to="/communication-creation" className="text-teal-dark font-medium">
                Communication
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Link to="/login">
                <Button variant="primary" className="hidden sm:inline-flex">
                  Se connecter
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-12 content-above-aurora relative z-10">

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-24"
          >
            <div className="flex justify-center mb-12">
              <div className="relative">
                <div className="w-28 h-28 bg-gradient-to-br from-teal-600 to-teal-800 rounded-3xl flex items-center justify-center shadow-2xl">
                  <MessageSquare className="w-14 h-14 text-white" />
                </div>
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-teal-light rounded-full flex items-center justify-center">
                  <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -bottom-2 -left-2 w-8 h-8 bg-teal-dark rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">AI</span>
                </div>
              </div>
            </div>
            <h1 className="text-7xl font-bold text-gray-900 mb-8 bg-gradient-to-r from-teal-dark via-teal-600 to-teal-light bg-clip-text text-transparent">
              Communication & Création
            </h1>
            <p className="text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-12">
              Révolutionnez votre communication avec l'IA. Créez du contenu engageant, 
              multilingue et adapté à vos audiences en quelques clics.
            </p>
            
            {/* Types de contenu */}
            <div className="flex flex-wrap justify-center gap-6 mb-16">
              <motion.div
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-teal-100"
              >
                <Type className="w-6 h-6 text-teal-dark mr-3" />
                <span className="text-lg font-semibold text-gray-700">Articles</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-teal-100"
              >
                <Video className="w-6 h-6 text-teal-dark mr-3" />
                <span className="text-lg font-semibold text-gray-700">Vidéos</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-teal-100"
              >
                <Image className="w-6 h-6 text-teal-dark mr-3" />
                <span className="text-lg font-semibold text-gray-700">Infographies</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-teal-100"
              >
                <MessageCircle className="w-6 h-6 text-teal-dark mr-3" />
                <span className="text-lg font-semibold text-gray-700">Posts Sociaux</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.1, y: -5 }}
                className="flex items-center bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-lg border border-teal-100"
              >
                <Mic className="w-6 h-6 text-teal-dark mr-3" />
                <span className="text-lg font-semibold text-gray-700">Podcasts</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Statistiques */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            <motion.div
              whileHover={{ scale: 1.05, y: -10 }}
              className="group"
            >
              <Card className="p-8 text-center bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100 hover:border-teal-200 transition-all duration-300 h-full">
                <div className="absolute top-4 right-4 w-3 h-3 bg-teal-light rounded-full animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-teal-light to-teal-dark rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow">
                  <Type className="w-10 h-10 text-white" />
                </div>
                <p className="text-5xl font-bold text-gray-900 mb-3">
                  <CountUp to={2500} duration={2} delay={0.2} />
                </p>
                <p className="text-xl font-semibold text-gray-700 mb-2">Contenus Créés</p>
                <p className="text-sm text-teal-600 font-medium">+40% ce mois</p>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, y: -10 }}
              className="group"
            >
              <Card className="p-8 text-center bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100 hover:border-teal-200 transition-all duration-300 h-full">
                <div className="absolute top-4 right-4 w-3 h-3 bg-teal-dark rounded-full animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-teal-dark to-teal-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow">
                  <Palette className="w-10 h-10 text-white" />
                </div>
                <p className="text-5xl font-bold text-gray-900 mb-3">
                  <CountUp to={15} duration={2} delay={0.4} />
                </p>
                <p className="text-xl font-semibold text-gray-700 mb-2">Formats Supportés</p>
                <p className="text-sm text-teal-600 font-medium">Multimédia complet</p>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, y: -10 }}
              className="group"
            >
              <Card className="p-8 text-center bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100 hover:border-teal-200 transition-all duration-300 h-full">
                <div className="absolute top-4 right-4 w-3 h-3 bg-teal-600 rounded-full animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow">
                  <Globe className="w-10 h-10 text-white" />
                </div>
                <p className="text-5xl font-bold text-gray-900 mb-3">
                  <CountUp to={12} duration={2} delay={0.6} />
                </p>
                <p className="text-xl font-semibold text-gray-700 mb-2">Langues Supportées</p>
                <p className="text-sm text-teal-600 font-medium">Traduction automatique</p>
              </Card>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.05, y: -10 }}
              className="group"
            >
              <Card className="p-8 text-center bg-gradient-to-br from-white to-teal-50 border-2 border-teal-100 hover:border-teal-200 transition-all duration-300 h-full">
                <div className="absolute top-4 right-4 w-3 h-3 bg-teal-800 rounded-full animate-pulse"></div>
                <div className="w-20 h-20 bg-gradient-to-br from-teal-800 to-teal-dark rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-xl transition-shadow">
                  <TrendingUp className="w-10 h-10 text-white" />
                </div>
                <p className="text-5xl font-bold text-gray-900 mb-3">
                  <CountUp to={95} duration={2} delay={0.8} />
                </p>
                <p className="text-xl font-semibold text-gray-700 mb-2">% d'Engagement</p>
                <p className="text-sm text-teal-600 font-medium">Performance exceptionnelle</p>
              </Card>
            </motion.div>
          </motion.div>

          {/* Workflow de Création */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-3xl p-12 shadow-2xl border border-gray-100"
          >
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Workflow de Création
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                De l'idée au contenu publié en 4 étapes simples et automatisées
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Étape 1 */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center group relative"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-light to-teal-dark rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-shadow">
                    <Search className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">1</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Recherche & Analyse</h3>
                <p className="text-gray-600 leading-relaxed">
                  L'IA analyse votre audience, les tendances et les meilleures pratiques pour votre secteur.
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="w-16 h-1 bg-gradient-to-r from-teal-light to-teal-dark rounded-full"></div>
                </div>
              </motion.div>

              {/* Étape 2 */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center group relative"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-dark to-teal-800 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-shadow">
                    <Edit3 className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-light rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">2</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Génération IA</h3>
                <p className="text-gray-600 leading-relaxed">
                  Création automatique du contenu adapté à votre marque et optimisé pour l'engagement.
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="w-16 h-1 bg-gradient-to-r from-teal-dark to-teal-800 rounded-full"></div>
                </div>
              </motion.div>

              {/* Étape 3 */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center group relative"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-600 to-teal-800 rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-shadow">
                    <Eye className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-dark rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">3</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Révision & Ajustement</h3>
                <p className="text-gray-600 leading-relaxed">
                  Personnalisation facile avec des suggestions intelligentes et des outils d'édition avancés.
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="w-16 h-1 bg-gradient-to-r from-teal-600 to-teal-800 rounded-full"></div>
                </div>
              </motion.div>

              {/* Étape 4 */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-center group relative"
              >
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-800 to-teal-dark rounded-2xl flex items-center justify-center mx-auto shadow-lg group-hover:shadow-xl transition-shadow">
                    <Send className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">4</span>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Publication & Suivi</h3>
                <p className="text-gray-600 leading-relaxed">
                  Publication automatique sur tous vos canaux et suivi des performances en temps réel.
                </p>
                <div className="mt-4 flex justify-center">
                  <div className="w-16 h-1 bg-gradient-to-r from-teal-800 to-teal-dark rounded-full"></div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Objectifs du Module */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Objectifs du Module
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-light rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Création Automatisée</h3>
                    <p className="text-gray-600">Générez du contenu de qualité en quelques clics avec l'IA avancée.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-dark rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Multimédia</h3>
                    <p className="text-gray-600">Créez des articles, vidéos, infographies et posts sociaux.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Multilingue</h3>
                    <p className="text-gray-600">Adaptez automatiquement votre contenu à 12 langues différentes.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-teal-800 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Optimisation SEO</h3>
                    <p className="text-gray-600">Contenu optimisé pour les moteurs de recherche et l'engagement.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-teal-light to-teal-dark rounded-2xl p-8 text-white">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <Type className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">2,500+</p>
                    <p className="text-sm opacity-80">Contenus</p>
                  </div>
                  <div className="text-center">
                    <Palette className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">15</p>
                    <p className="text-sm opacity-80">Formats</p>
                  </div>
                  <div className="text-center">
                    <Globe className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">12</p>
                    <p className="text-sm opacity-80">Langues</p>
                  </div>
                  <div className="text-center">
                    <TrendingUp className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <p className="text-2xl font-bold">95%</p>
                    <p className="text-sm opacity-80">Engagement</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Impact dans l'Organisation */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="relative overflow-hidden"
          >
            <div className="bg-gradient-to-br from-teal-dark via-teal-600 to-teal-800 rounded-3xl p-12 text-white relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
                <div className="absolute bottom-10 right-10 w-24 h-24 border-2 border-white rounded-full"></div>
                <div className="absolute top-1/2 left-1/4 w-16 h-16 border-2 border-white rounded-full"></div>
                <div className="absolute top-1/4 right-1/3 w-20 h-20 border-2 border-white rounded-full"></div>
              </div>
              
              <div className="relative z-10">
                <h2 className="text-4xl font-bold mb-4 text-center">
                  Impact dans l'Organisation
                </h2>
                <p className="text-xl text-teal-100 text-center mb-12 max-w-3xl mx-auto">
                  Transformez votre communication avec des résultats mesurables et un ROI exceptionnel
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  <motion.div 
                    className="text-center group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="relative mb-6">
                      <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto group-hover:bg-white/30 transition-colors">
                        <Zap className="w-12 h-12 text-white" />
                      </div>
                      <div className="absolute -top-3 -right-3 w-10 h-10 bg-teal-light rounded-full flex items-center justify-center">
                        <span className="text-white text-lg font-bold">300%</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Productivité</h3>
                    <p className="text-teal-100 leading-relaxed">
                      Augmentation de <strong className="text-white">300%</strong> de la production de contenu. 
                      Création 10x plus rapide avec l'IA.
                    </p>
                  </motion.div>
                  
                  <motion.div 
                    className="text-center group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="relative mb-6">
                      <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto group-hover:bg-white/30 transition-colors">
                        <Target className="w-12 h-12 text-white" />
                      </div>
                      <div className="absolute -top-3 -right-3 w-10 h-10 bg-teal-light rounded-full flex items-center justify-center">
                        <span className="text-white text-lg font-bold">95%</span>
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-3">Engagement</h3>
                    <p className="text-teal-100 leading-relaxed">
                      Amélioration de <strong className="text-white">95%</strong> du taux d'engagement. 
                      Contenu personnalisé et pertinent.
                    </p>
                  </motion.div>
                  
                  <motion.div 
                    className="text-center group"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="relative mb-6">
                      <div className="w-24 h-24 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto group-hover:bg-white/30 transition-colors">
                        <Globe className="w-12 h-12 text-white" />
                      </div>
                      <div className="absolute -top-3 -right-3 w-10 h-10 bg-teal-light rounded-full flex items-center justify-center">
                        <span className="text-white text-lg font-bold">12</span>
                      </div>
                </div>
                    <h3 className="text-2xl font-bold mb-3">Portée</h3>
                    <p className="text-teal-100 leading-relaxed">
                      Expansion <strong className="text-white">internationale</strong> avec contenu multilingue. 
                      12 langues supportées automatiquement.
                    </p>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Avantages Clés */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Avantages Clés
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-light bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-6 h-6 text-teal-dark" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Multimédia</h3>
                <p className="text-sm text-gray-600">Articles, vidéos, infographies</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-6 h-6 text-teal-dark" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Multilingue</h3>
                <p className="text-sm text-gray-600">12 langues supportées</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-600 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Filter className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Personnalisation</h3>
                <p className="text-sm text-gray-600">Contenu adapté à votre audience</p>
              </Card>
              
              <Card className="p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-teal-800 bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <Upload className="w-6 h-6 text-teal-800" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Publication</h3>
                <p className="text-sm text-gray-600">Publication automatique sur tous les canaux</p>
              </Card>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="text-center py-12"
          >
            <div className="bg-gradient-to-r from-teal-dark to-teal-light rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4">
                Prêt à Révolutionner votre Communication ?
              </h2>
              <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                Découvrez comment notre module peut transformer votre création de contenu 
                et amplifier votre présence digitale avec l'IA.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/login" className="bg-white text-teal-dark hover:bg-gray-100 hover:scale-105 hover:shadow-lg font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center">Accéder à la plateforme</Link>
              </div>
            </div>
          </motion.div>

        </div>
        </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <motion.div 
                className="flex items-center mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Logo className="w-16 h-16 mr-3" />
                <span className="text-xl font-bold">omnia-eg</span>
              </motion.div>
              <motion.p 
                className="text-gray-400 mb-6 max-w-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                La plateforme IA qui révolutionne la gestion documentaire et la rédaction stratégique pour les organisations modernes.
              </motion.p>
              <motion.div 
                className="flex space-x-4"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Globe className="w-5 h-5 text-teal-light" />
                </div>
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Mail className="w-5 h-5 text-teal-light" />
                </div>
                <div className="w-10 h-10 bg-teal-dark bg-opacity-20 rounded-lg flex items-center justify-center hover:bg-opacity-30 transition-colors cursor-pointer">
                  <Phone className="w-5 h-5 text-teal-light" />
              </div>
              </motion.div>
            </div>
            
            <div>
              <motion.h3 
                className="font-semibold mb-6 text-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                Solutions
              </motion.h3>
              <motion.ul 
                className="space-y-3 text-gray-400"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <li><Link to="/document-management" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Gestion Documentaire</Link></li>
                <li><Link to="/strategic-writing" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Rédaction Stratégique</Link></li>
                <li><Link to="/communication-creation" className="hover:text-white transition-colors text-left hover:translate-x-1 transform block">Communication & Création</Link></li>
              </motion.ul>
            </div>
            

            

                    </div>
          
          <motion.div 
            className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            viewport={{ once: true }}
          >
            <p className="text-gray-400 text-sm mb-4 md:mb-0">
              © 2026 OmnIA-EG. Tous droits réservés.
            </p>
            <div className="flex space-x-6 text-sm text-gray-400">
                  </div>
                </motion.div>
                </div>
      </footer>
      </div>
  );
};

export default CommunicationCreationPage;